import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, UploadCloud, FileVideo, Loader2, ShieldCheck, X,
  Sparkles, AlertTriangle, CheckCircle2, Info, Camera, Video,
} from "lucide-react";

/**
 * Platform-owner-only calibration tester for the AI Vehicle Health Check
 * pipeline. Drag-drop a workshop walkaround video → see exactly what the
 * pipeline would extract, without touching any job or dealership data.
 *
 * Used during the pre-rollout calibration phase. Hidden from dealers — there's
 * no link from any non-admin page.
 */
const URGENCY_PILL = {
  red:   { bg: "bg-destructive/15",    text: "text-destructive", border: "border-destructive/40", label: "RED" },
  amber: { bg: "bg-warning/15",        text: "text-warning",     border: "border-warning/40",     label: "AMBER" },
  green: { bg: "bg-success/15",        text: "text-success",     border: "border-success/40",     label: "GREEN" },
};

function fmtBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtPct(x) {
  return `${Math.round((Number(x) || 0) * 100)}%`;
}

function fmtSec(s) {
  const v = Number(s) || 0;
  if (v < 60) return `${v.toFixed(1)}s`;
  return `${Math.floor(v / 60)}m ${(v % 60).toFixed(0)}s`;
}

export default function AiVhcTesterPage() {
  const nav = useNavigate();
  const fileInputRef = useRef(null);
  const [me, setMe] = useState(null);
  const [authError, setAuthError] = useState("");
  // "video" = walkaround pipeline · "photos" = still-photo pipeline (no ffmpeg, faster)
  const [mode, setMode] = useState("video");
  const [file, setFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [photos, setPhotos] = useState([]);       // list of {file, url} for the photos mode
  const [note, setNote] = useState("");            // optional technician caption for photos mode
  const [dragOver, setDragOver] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Confirm the user is a platform owner — every call to /admin/* 403s otherwise.
  useEffect(() => {
    api.get("/auth/me").then((r) => {
      setMe(r.data);
      if (!r.data?.platform_owner) setAuthError("This calibration tester is platform-owner only.");
    }).catch(() => setAuthError("Please log in to use this page."));
  }, []);

  // Revoke any object URLs when the photos list changes / component unmounts to
  // avoid leaking blob URLs across long calibration sessions.
  useEffect(() => () => photos.forEach((p) => URL.revokeObjectURL(p.url)), [photos]);

  const pickVideo = (f) => {
    if (!f) return;
    if (!f.type.startsWith("video/") && !/\.(mp4|webm|mov|m4v)$/i.test(f.name)) {
      toast.error("That doesn't look like a video file.");
      return;
    }
    if (f.size > 200 * 1024 * 1024) {
      toast.error(`Video is ${fmtBytes(f.size)} — limit is 200 MB.`);
      return;
    }
    setFile(f);
    setResult(null);
    setError("");
  };

  const pickPhotos = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const incoming = Array.from(fileList).slice(0, 8 - photos.length);
    const accepted = [];
    for (const f of incoming) {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name} isn't an image.`);
        continue;
      }
      accepted.push({ file: f, url: URL.createObjectURL(f) });
    }
    if (!accepted.length) return;
    setPhotos((prev) => [...prev, ...accepted].slice(0, 8));
    setResult(null);
    setError("");
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => {
      const gone = prev[idx];
      if (gone) URL.revokeObjectURL(gone.url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (mode === "photos") pickPhotos(e.dataTransfer?.files);
    else pickVideo(e.dataTransfer?.files?.[0]);
  };

  const pollTesterRun = async (runId, kind) => {
    // Poll ai-vhc-tester/status/{run_id} every 2s for up to 5 min. Cloudflare's
    // 100s edge cap makes a synchronous wait impossible on production, so the
    // API returns {run_id, state:"running"} immediately and we poll here.
    const started = Date.now();
    while (Date.now() - started < 5 * 60 * 1000) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const s = await api.get(`/admin/ai-vhc-tester/status/${runId}`);
        if (s.data.state === "done") {
          const shaped = kind === "photos"
            ? { mode: "photos", photo_count: s.data.photo_count, size_bytes: s.data.size_bytes,
                elapsed_sec: s.data.elapsed_sec, note: s.data.note, ...(s.data.result || {}) }
            : { ok: true, filename: s.data.filename, size_bytes: s.data.size_bytes,
                content_type: s.data.content_type, elapsed_sec: s.data.elapsed_sec,
                ...(s.data.result || {}) };
          setResult(shaped);
          toast.success(`Pipeline finished in ${s.data.elapsed_sec}s · ${shaped.findings?.length || 0} findings`);
          return;
        }
        if (s.data.state === "failed") {
          setError(s.data.error || "Pipeline failed.");
          toast.error("Couldn't analyse — see error below.");
          return;
        }
      } catch (e) {
        // transient — keep polling until we hit the wall-clock limit
      }
    }
    setError("Pipeline is taking longer than 5 minutes — try a shorter clip or fewer photos.");
    toast.error("Timed out waiting for analysis.");
  };

  const analyse = async () => {
    setRunning(true);
    setError("");
    setResult(null);
    try {
      if (mode === "photos") {
        if (!photos.length) return;
        const fd = new FormData();
        photos.forEach((p) => fd.append("files", p.file));
        fd.append("note", note || "");
        const r = await api.post("/admin/ai-vhc-tester/analyse-photos", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        });
        if (r.data.run_id) {
          await pollTesterRun(r.data.run_id, "photos");
        } else {
          setResult(r.data);
          toast.success(`Vision run finished in ${r.data.elapsed_sec}s · ${r.data.findings?.length || 0} findings`);
        }
      } else {
        if (!file && !videoUrl.trim()) return;
        const fd = new FormData();
        if (file) fd.append("file", file);
        else fd.append("video_url", videoUrl.trim());
        const r = await api.post("/admin/ai-vhc-tester/analyse", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000,
        });
        if (r.data.run_id) {
          await pollTesterRun(r.data.run_id, "video");
        } else {
          setResult(r.data);
          toast.success(`Pipeline finished in ${r.data.elapsed_sec}s · ${r.data.findings?.length || 0} findings`);
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Pipeline failed.";
      setError(typeof msg === "string" ? msg : "Pipeline failed.");
      toast.error("Couldn't analyse — see error below.");
    } finally {
      setRunning(false);
    }
  };

  const reset = () => {
    photos.forEach((p) => URL.revokeObjectURL(p.url));
    setPhotos([]);
    setNote("");
    setFile(null);
    setResult(null);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const switchMode = (m) => {
    if (m === mode) return;
    reset();
    setMode(m);
  };

  if (authError) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
        <div className="max-w-md text-center space-y-4" data-testid="ai-vhc-tester-auth-error">
          <ShieldCheck className="w-12 h-12 text-warning mx-auto" />
          <h1 className="font-display font-bold text-xl">Restricted</h1>
          <p className="text-sm text-muted-foreground">{authError}</p>
          <button onClick={() => nav("/dashboard")} className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-bold text-sm">
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="ai-vhc-tester-page">
      {/* Header */}
      <header className="border-b border-border bg-card/40 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-4">
          <Link to="/accounts" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="ai-vhc-tester-back">
            <ArrowLeft className="w-4 h-4" /> Back to accounts
          </Link>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <h1 className="font-display font-bold text-lg" data-testid="ai-vhc-tester-title">AI VHC · calibration tester</h1>
              <span className="text-[10px] font-mono uppercase tracking-wider text-warning border border-warning/40 rounded px-1.5 py-0.5 ml-1">PLATFORM OWNER</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Try the pipeline with a real walkaround <em>or</em> just a few defect photos + a caption. No DB writes, no jobs touched.
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Mode toggle — choose Video (full pipeline) vs Photos (vision-only, faster) */}
        <div className="inline-flex items-center gap-0 rounded-lg border border-white/15 bg-white/[0.02] p-1" data-testid="ai-vhc-tester-mode-toggle">
          <button
            onClick={() => switchMode("video")}
            disabled={running}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold transition-colors ${mode === "video" ? "bg-primary text-primary-foreground" : "text-white/60 hover:text-white"}`}
            data-testid="ai-vhc-tester-mode-video"
          >
            <Video className="w-3.5 h-3.5" /> Video walkaround
          </button>
          <button
            onClick={() => switchMode("photos")}
            disabled={running}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold transition-colors ${mode === "photos" ? "bg-primary text-primary-foreground" : "text-white/60 hover:text-white"}`}
            data-testid="ai-vhc-tester-mode-photos"
          >
            <Camera className="w-3.5 h-3.5" /> Photos + caption
          </button>
        </div>
        {/* Drop zone (video mode) */}
        {mode === "video" && !file && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`rounded-lg border-2 border-dashed transition-colors cursor-pointer p-12 text-center
              ${dragOver ? "border-primary bg-primary/5" : "border-white/15 bg-white/[0.02] hover:border-white/30"}`}
            data-testid="ai-vhc-tester-dropzone"
          >
            <UploadCloud className="w-12 h-12 text-primary mx-auto mb-3" />
            <div className="font-display font-bold text-base">Drag a workshop walkaround here</div>
            <p className="text-sm text-muted-foreground mt-1">…or click to pick a file. MP4, WebM, MOV up to 200&nbsp;MB.</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*,.mp4,.webm,.mov,.m4v"
              className="hidden"
              onChange={(e) => pickVideo(e.target.files?.[0])}
              data-testid="ai-vhc-tester-file-input"
            />
          </div>
        )}

        {/* Or analyse straight from a link — CitNOW, eDynamix, direct MP4 */}
        {mode === "video" && !file && !result && (
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 flex flex-wrap items-center gap-3" data-testid="ai-vhc-tester-url-row">
            <span className="text-[11px] font-mono uppercase tracking-wider text-white/40 shrink-0">or paste a video link</span>
            <input
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://video.citnow.com/…  ·  eDynamix share link  ·  direct .mp4"
              className="flex-1 min-w-[240px] bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm"
              data-testid="ai-vhc-tester-url-input"
            />
            <button
              onClick={analyse}
              disabled={running || !videoUrl.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
              data-testid="ai-vhc-tester-url-run-btn"
            >
              {running ? <><Loader2 className="w-4 h-4 animate-spin" /> Running…</> : <><Sparkles className="w-4 h-4" /> Analyse link</>}
            </button>
          </div>
        )}

        {/* Drop zone (photos mode) — always visible so more photos can be added incrementally */}
        {mode === "photos" && (
          <div className="space-y-4" data-testid="ai-vhc-tester-photos-area">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`rounded-lg border-2 border-dashed transition-colors cursor-pointer p-8 text-center
                ${dragOver ? "border-primary bg-primary/5" : "border-white/15 bg-white/[0.02] hover:border-white/30"}
                ${photos.length >= 8 ? "opacity-40 pointer-events-none" : ""}`}
              data-testid="ai-vhc-tester-photos-dropzone"
            >
              <Camera className="w-9 h-9 text-primary mx-auto mb-2" />
              <div className="font-display font-bold text-base">
                {photos.length === 0 ? "Drop 1-8 defect photos" : `Add more (${photos.length}/8)`}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                …or click to browse. JPG / PNG / HEIC. Max 200&nbsp;MB total.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => pickPhotos(e.target.files)}
                data-testid="ai-vhc-tester-photos-input"
              />
            </div>

            {/* Photo thumbnails */}
            {photos.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ai-vhc-tester-photo-thumbs">
                {photos.map((p, i) => (
                  <div key={p.url} className="relative aspect-video rounded-md border border-white/10 bg-black/40 overflow-hidden group">
                    <img src={p.url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                    <div className="absolute bottom-1 left-1 text-[10px] font-mono bg-black/70 rounded px-1 py-0.5 text-white/80">
                      #{i + 1} · {fmtBytes(p.file.size)}
                    </div>
                    <button
                      onClick={() => removePhoto(i)}
                      disabled={running}
                      className="absolute top-1 right-1 bg-black/80 text-white/80 hover:text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-30"
                      data-testid={`ai-vhc-tester-photo-remove-${i}`}
                      title="Remove photo"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Optional technician caption */}
            {photos.length > 0 && (
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-white/50 block mb-1.5" htmlFor="ai-vhc-tester-note">
                  Technician note (optional but recommended)
                </label>
                <textarea
                  id="ai-vhc-tester-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  disabled={running}
                  rows={3}
                  placeholder="e.g. Front pads down to 2mm, discs scored — customer said pulsing under braking."
                  className="w-full rounded-md border border-white/15 bg-white/[0.02] px-3 py-2 text-sm placeholder:text-white/30 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors resize-y"
                  data-testid="ai-vhc-tester-note-input"
                />
                <p className="text-[10px] text-white/40 mt-1">
                  The AI treats this as ground truth and uses the photos to confirm/refine. Leave blank to let it infer purely from the images.
                </p>
              </div>
            )}

            {/* Run button */}
            {photos.length > 0 && !result && (
              <div className="flex items-center gap-3">
                <button
                  onClick={analyse}
                  disabled={running}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="ai-vhc-tester-photos-run-btn"
                >
                  {running
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending to Claude…</>
                    : <><Sparkles className="w-4 h-4" /> Analyse {photos.length} photo{photos.length === 1 ? "" : "s"}</>}
                </button>
                <button
                  onClick={reset}
                  disabled={running}
                  className="text-xs text-white/60 hover:text-white disabled:opacity-30 transition-colors"
                  data-testid="ai-vhc-tester-photos-reset"
                >
                  Start over
                </button>
                {running && (
                  <span className="text-[11px] text-white/50">typically 5-15 seconds</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Picked-file summary + run button (video mode only) */}
        {mode === "video" && file && !result && (
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-5" data-testid="ai-vhc-tester-staged">
            <div className="flex items-start gap-4">
              <FileVideo className="w-8 h-8 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate" data-testid="ai-vhc-tester-staged-filename">{file.name}</div>
                <div className="text-[11px] font-mono text-white/50 mt-0.5">
                  {fmtBytes(file.size)} · {file.type || "video/*"}
                </div>
                {!running && (
                  <p className="text-[11px] text-white/40 mt-2">
                    Pipeline runs at roughly 1× realtime — a 60-sec video should be ready in ~60-90 seconds.
                  </p>
                )}
              </div>
              <button
                onClick={reset}
                disabled={running}
                className="text-white/50 hover:text-white transition-colors p-1 disabled:opacity-30"
                data-testid="ai-vhc-tester-clear"
                title="Drop a different file"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={analyse}
                disabled={running}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="ai-vhc-tester-run-btn"
              >
                {running
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Running pipeline…</>
                  : <><Sparkles className="w-4 h-4" /> Run AI VHC pipeline</>}
              </button>
              {running && (
                <span className="text-[11px] text-white/50">
                  stabilise · denoise · transcribe · sample frames · vision extract
                </span>
              )}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 flex items-start gap-3" data-testid="ai-vhc-tester-error">
            <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-destructive text-sm">Pipeline failed</div>
              <div className="text-[12px] text-white/70 mt-0.5 break-words">{error}</div>
            </div>
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="space-y-6" data-testid="ai-vhc-tester-result">
            {/* Top summary tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {result.mode === "photos" ? (
                <>
                  <SummaryTile label="Photos" value={`${result.photo_count || 0}`} />
                  <SummaryTile label="Pipeline time" value={fmtSec(result.elapsed_sec)} />
                  <SummaryTile label="Caption" value={`${(result.note || "").length} chars`} />
                  <SummaryTile label="Findings" value={`${result.findings?.length || 0}`} accent={(result.findings?.length || 0) > 0} />
                </>
              ) : (
                <>
                  <SummaryTile label="Duration" value={fmtSec(result.duration_sec)} />
                  <SummaryTile label="Pipeline time" value={fmtSec(result.elapsed_sec)} />
                  <SummaryTile label="Transcript" value={`${(result.transcript || "").length} chars`} />
                  <SummaryTile label="Findings" value={`${result.findings?.length || 0}`} accent={(result.findings?.length || 0) > 0} />
                </>
              )}
            </div>

            {/* Component checklist — the industry-leading bit */}
            {(result.component_checklist || []).length > 0 && (
              <div data-testid="ai-vhc-tester-checklist">
                <div className="text-[11px] font-mono uppercase tracking-wider text-white/40 mb-2">
                  Component checklist — every component assessed, even unmentioned ones
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {result.component_checklist.map((c, i) => {
                    const cls = c.status === "urgent" ? "border-destructive/60 text-destructive"
                      : c.status === "advisory" ? "border-warning/60 text-warning"
                      : c.status === "good" ? "border-success/60 text-success"
                      : "border-white/15 text-white/40";
                    return (
                      <div key={i} className={`rounded-md border bg-white/[0.02] p-3 ${cls}`} data-testid={`checklist-${c.component}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-bold text-xs uppercase tracking-wider">{c.label || c.component}</span>
                          <span className="text-[10px] font-mono">{(c.status || "").replace("_", " ")}{c.source === "visual" ? " · 👁 visual" : ""}</span>
                        </div>
                        {c.evidence && <div className="text-[11px] text-white/60 mt-1">{c.evidence}</div>}
                        {c.tread_mm && (
                          <div className="text-[10px] font-mono text-white/50 mt-1">
                            {Object.entries(c.tread_mm).filter(([, v]) => v != null).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}mm`).join(" · ")}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Top action row */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="text-[11px] font-mono text-white/50 truncate" data-testid="ai-vhc-tester-source-info">
                {result.mode === "photos"
                  ? `${result.photo_count || 0} photo${result.photo_count === 1 ? "" : "s"} · ${fmtBytes(result.size_bytes)}`
                  : `${result.filename} · ${fmtBytes(result.size_bytes)} · ${result.content_type || "video/*"}`}
              </div>
              <button
                onClick={reset}
                className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white border border-white/15 rounded-md px-3 py-1.5 transition-colors"
                data-testid="ai-vhc-tester-run-another"
              >
                <UploadCloud className="w-4 h-4" /> Try {result.mode === "photos" ? "another set" : "another video"}
              </button>
            </div>

            {/* Transcript (video) OR technician caption (photos) */}
            {result.mode !== "photos" && (
            <section className="rounded-lg border border-white/10 bg-white/[0.02] p-5">
              <div className="flex items-center gap-2 mb-3">
                <Info className="w-4 h-4 text-primary" />
                <h2 className="font-display font-bold text-sm">Cleaned transcript</h2>
                <span className="text-[10px] font-mono text-white/40">{result.segments?.length || 0} segments</span>
              </div>
              {result.transcript ? (
                <p className="text-sm text-white/85 leading-relaxed whitespace-pre-wrap" data-testid="ai-vhc-tester-transcript">
                  {result.transcript}
                </p>
              ) : (
                <p className="text-sm text-white/40 italic">No speech detected after noise suppression.</p>
              )}
              {result.segments?.length > 0 && (
                <details className="mt-3">
                  <summary className="text-[11px] text-white/50 cursor-pointer hover:text-white/80">Segment timestamps</summary>
                  <ul className="text-[11px] font-mono text-white/60 mt-2 space-y-1 max-h-60 overflow-y-auto">
                    {result.segments.map((s, i) => (
                      <li key={i}>
                        <span className="text-white/40">[{s.start?.toFixed(1)}s – {s.end?.toFixed(1)}s]</span>{" "}
                        {s.text}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </section>
            )}

            {/* Show the technician caption we sent, for the photos flow */}
            {result.mode === "photos" && result.note && (
              <section className="rounded-lg border border-white/10 bg-white/[0.02] p-5" data-testid="ai-vhc-tester-caption">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="w-4 h-4 text-primary" />
                  <h2 className="font-display font-bold text-sm">Technician caption sent to Claude</h2>
                </div>
                <p className="text-sm text-white/85 leading-relaxed whitespace-pre-wrap">{result.note}</p>
              </section>
            )}

            {/* Findings list */}
            <section className="rounded-lg border border-white/10 bg-white/[0.02] p-5">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-primary" />
                <h2 className="font-display font-bold text-sm">Structured findings</h2>
              </div>
              {result.findings?.length === 0 ? (
                <p className="text-sm text-white/50">
                  Pipeline ran cleanly but extracted no findings. Either the transcript didn&rsquo;t mention any recommended work,
                  or every candidate finding was below the confidence floor.
                </p>
              ) : (
                <ul className="divide-y divide-white/5" data-testid="ai-vhc-tester-findings">
                  {result.findings.map((f, i) => {
                    const pill = URGENCY_PILL[f.urgency] || URGENCY_PILL.amber;
                    return (
                      <li key={i} className="py-4 space-y-2" data-testid={`ai-vhc-tester-finding-${i}`}>
                        <div className="flex items-baseline flex-wrap gap-2">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${pill.bg} ${pill.text} ${pill.border}`}>
                            {pill.label}
                          </span>
                          <span className="font-semibold">{f.name}</span>
                          <span className="text-sm text-white/70">·</span>
                          <span className="font-mono text-sm">£{Number(f.unit_price || 0).toFixed(2)}</span>
                          <span className="text-[11px] text-white/50">× {f.quantity || 1}</span>
                          <span className="flex-1" />
                          <span className="text-[10px] font-mono text-white/50">
                            conf {fmtPct(f.confidence)} · @{Number(f.timestamp_sec || 0).toFixed(1)}s
                          </span>
                        </div>
                        {f.category && (
                          <div className="text-[10.5px] font-mono text-white/40">
                            category: {f.category}
                          </div>
                        )}
                        {f.tech_quote && (
                          <div className="text-[11.5px] text-white/65 border-l-2 border-white/15 pl-3 italic">
                            “{f.tech_quote}”
                          </div>
                        )}
                        {f.description && (
                          <div className="text-[12px] text-white/85 leading-relaxed bg-white/[0.03] rounded p-3">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-primary mr-2">Customer-facing copy</span>
                            {f.description}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {result.findings?.length > 0 && (
              <div className="rounded-md border border-primary/30 bg-primary/5 p-4 flex items-start gap-3" data-testid="ai-vhc-tester-tip">
                <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div className="text-[12px] text-white/80">
                  <strong>Calibration tip.</strong> If any finding is wrong, note (a) what the tech actually said, (b) what came out, and
                  send that to the dev so the prompt can be tuned. Each correction makes the model sharper across every future video.
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function SummaryTile({ label, value, accent = false }) {
  return (
    <div className={`rounded-lg border p-4 ${accent ? "border-primary/40 bg-primary/5" : "border-white/10 bg-white/[0.02]"}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-white/50">{label}</div>
      <div className={`mt-1 font-display font-bold text-2xl ${accent ? "text-primary" : "text-white"}`}>{value}</div>
    </div>
  );
}
